import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loading } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { useMiniApps } from '@/pages/conversations/embedded-apps-dialogs'
import type { MiniApp } from '@/pages/conversations/app-rail'
import { APP_CATEGORIES, APPS, type AppCategory, type AppDef } from './apps-catalog'

/** Ô icon của ứng dụng: ảnh nếu có, không thì chữ viết tắt trên nền màu thương hiệu. */
export function AppLogo({ app, size = 'md' }: { app: Pick<AppDef, 'icon' | 'glyph' | 'brand' | 'name'>; size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-12 w-12 rounded-xl text-sm' : 'h-9 w-9 rounded-lg text-[11px]'
  if (app.icon) {
    return (
      // Nền trắng cả ở dark mode để logo thương hiệu giữ đúng màu và đủ tương phản.
      <span className={cn('flex shrink-0 items-center justify-center border border-black/10 bg-white', box)}>
        <img src={app.icon} alt="" draggable={false} className={cn('object-contain', size === 'lg' ? 'h-8 w-8' : 'h-6 w-6')} />
      </span>
    )
  }
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center font-bold text-white', box)}
      style={{ backgroundColor: app.brand }}
    >
      {app.glyph ?? app.name.charAt(0)}
    </span>
  )
}

/**
 * Kết nối → Ứng dụng: "Kho ứng dụng" + "Ứng dụng của tôi", theo mẫu App Store
 * của SaleSmartly. Ứng dụng đã cài = mini app đang bật ở cột phải Hội thoại.
 */
export function AppsPage() {
  const { t } = useTranslation('chanCatalog')
  const { apps: mine, isLoading } = useMiniApps()
  const [tab, setTab] = useState<'store' | 'mine'>('store')
  const [cat, setCat] = useState<AppCategory | 'all'>('all')

  const installed = (a: AppDef) => !!a.embedId && mine.some((m) => m.id === a.embedId && m.active)
  const shown = cat === 'all' ? APPS : APPS.filter((a) => a.categories.includes(cat))

  return (
    <div className="space-y-5">
      <h1 className="flex h-14 items-center text-lg font-medium">{t('appsPage.title')}</h1>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'store' | 'mine')}>
        <TabsList>
          <TabsTrigger value="store">{t('appsPage.tabStore')}</TabsTrigger>
          <TabsTrigger value="mine">{t('appsPage.tabMine', { count: mine.filter((m) => m.active).length })}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'store' ? (
        <>
          <div className="flex flex-wrap gap-2">
            {APP_CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCat(c.value)}
                className={cn(
                  'h-8 rounded-md px-3 text-sm transition-colors',
                  cat === c.value ? 'bg-accent font-medium text-primary' : 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
                )}
              >
                {c.label}
                <span className="ml-1 text-muted-foreground">
                  ({c.value === 'all' ? APPS.length : APPS.filter((a) => a.categories.includes(c.value as AppCategory)).length})
                </span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {shown.map((a) => (
              <AppCard key={a.id} app={a} installed={installed(a)} />
            ))}
          </div>
        </>
      ) : isLoading ? (
        <Loading />
      ) : (
        <MyApps apps={mine} />
      )}
    </div>
  )
}

function AppCard({ app, installed }: { app: AppDef; installed: boolean }) {
  const { t } = useTranslation('chanCatalog')
  const navigate = useNavigate()
  return (
    <div
      className="relative flex h-[190px] flex-col rounded-lg border p-4 transition-shadow hover:shadow-md"
      style={{ backgroundImage: `linear-gradient(90deg, ${app.brand}14 0%, hsl(var(--card)) 45%)` }}
    >
      {app.beta && (
        <span className="absolute right-0 top-0 flex items-center gap-1 rounded-bl-lg rounded-tr-lg bg-primary/10 px-2 py-0.5 text-xs text-primary">
          <Sparkles className="h-3 w-3" /> Beta
        </span>
      )}
      <Link to={`/integrations/apps/${app.id}`} className="flex items-center gap-2.5">
        <AppLogo app={app} />
        <span className="truncate font-medium">{app.name}</span>
      </Link>
      <p className="mt-3 line-clamp-2 text-xs leading-5 text-muted-foreground">{app.description}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t('appsPage.developer', { name: app.developer })}</p>
      <div className="mt-auto flex items-center justify-end gap-2">
        {installed && (
          <span className="mr-auto flex items-center gap-1 text-xs text-success">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t('appsPage.installed')}
          </span>
        )}
        <Button variant="secondary" onClick={() => navigate(`/integrations/apps/${app.id}`)}>
          {installed ? t('common:actions.manage') : app.beta ? t('appsPage.viewNow') : t('appsPage.useNow')}
        </Button>
      </div>
    </div>
  )
}

function MyApps({ apps }: { apps: MiniApp[] }) {
  const { t } = useTranslation('chanCatalog')
  const navigate = useNavigate()
  const list = apps.filter((a) => a.active)
  if (!list.length) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        {t('appsPage.noneInstalled')}
      </p>
    )
  }
  return (
    <div className="divide-y rounded-lg border bg-card">
      {list.map((m) => {
        const def = APPS.find((a) => a.embedId === m.id)
        return (
          <div key={m.id} className="flex items-center gap-3 p-4">
            <AppLogo app={def ?? { name: m.name, icon: m.icon, brand: '#475569' }} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{m.name}</p>
              <p className="truncate text-xs text-muted-foreground">{t('appsPage.embedForm')} · {m.url}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/integrations/apps/${def?.id ?? 'webview'}`)}>
              {t('common:actions.manage')}
            </Button>
          </div>
        )
      })}
    </div>
  )
}
